/**
 * country-gate.js — reveals copy that is only true in some countries.
 *
 * A gated claim is rendered `hidden` and this upgrades it. Hidden-by-default is the whole
 * point, and it is the opposite of the usual progressive-enhancement direction: the server
 * must promise nothing, because the failure that matters is a German shopper being told
 * they can collect from a local dealer when there is no dealer network in Germany. A claim
 * that appears during load and then disappears has already been read.
 *
 * The server already rendered the claim openly where the shopper's market country is one it
 * names (blocks/usp-item.liquid), so what reaches this script is a claim the market does not
 * make true. It is revealed only when ValerydDealer.country() (assets/dealer-context.js) names
 * a country in the gate: the market's pinned dealer country, or the visitor's own when the
 * dealer fallback list allows it. That is one lookup per page through ValerydGeo, shared with
 * every other dealer surface. With nothing gated on the page it asks for nothing at all.
 *
 * Failure is silent and safe in one direction only: an unknown country leaves the page
 * exactly as rendered, with the gated claim still hidden.
 *
 * `[data-dealer-absent]` is the inverse, for the invitation to become a dealer
 * (snippets/dealer-teaser.liquid): revealed when country() names no country, i.e. where this
 * page shows the visitor no dealers. Its failure runs the other way on purpose: a lookup that
 * fails shows the invitation to someone who may have dealers. That costs a line of copy that is
 * true anywhere; it never claims a dealer exists.
 */
(() => {
  const gated = Array.from(document.querySelectorAll('[data-country-gate]'));
  const absent = Array.from(document.querySelectorAll('[data-dealer-absent]'));
  if (!gated.length && !absent.length) return; // nothing to reveal → no network call

  // The rendered list IS the source of truth, exactly as regnr_countries is for the
  // plate band: an element that names no country can never be revealed.
  function allows(el, country) {
    return (el.dataset.countryGate || '')
      .split(',')
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean)
      .includes(country);
  }

  const dealer = window.ValerydDealer;
  if (!dealer) return;
  for (const el of gated) {
    dealer.country(el).then((country) => {
      if (country && allows(el, country)) el.hidden = false; // null → stays hidden
    });
  }
  for (const el of absent) {
    dealer.country(el).then((country) => {
      if (!country) el.hidden = false;
    });
  }
})();
